import { useCallback, useEffect, useRef, useState } from 'react';
import { transcribeAudio } from '../services/api';

interface UseVoiceRecorderOptions {
  /** Called with the transcribed text once recording stops and STT completes. */
  onResult: (transcript: string) => void;
  /** Custom STT call (e.g. teacher-authed `transcribeAudioAsTeacher`).
   *  Defaults to the student-authed `transcribeAudio`. */
  transcribe?: (audio: Blob, filename: string) => Promise<string>;
}

interface UseVoiceRecorderReturn {
  isSupported: boolean;
  isRecording: boolean;
  isTranscribing: boolean;
  error: string | null;
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  toggleRecording: () => void;
}

// --- Voice-activity-detection (auto-stop) tuning --------------------------
// Stop automatically once the student has spoken and then goes quiet for this
// long. ~1.5s tolerates natural mid-sentence pauses without cutting off early.
const SILENCE_HANG_MS = 1500;
// RMS (0..1) above which a frame is considered speech rather than background.
const SPEECH_RMS_THRESHOLD = 0.015;
// How often we sample the mic level.
const VAD_INTERVAL_MS = 100;
// Safety caps: never record longer than this, and give up if no speech starts.
const MAX_RECORDING_MS = 30000;
const NO_SPEECH_TIMEOUT_MS = 8000;
// Ignore the first moments after the mic opens: the open "pop", the button
// click and auto-gain settling all spike the level and used to count as
// speech, so the silence timer stopped the clip before the speaker began.
const VAD_WARMUP_MS = 300;
// Speech must be sustained this long (summed loud frames) before we treat the
// speaker as having started. A lone loud frame is a click, not a word.
const MIN_SPEECH_MS = 300;

const isRecorderSupported = (): boolean =>
  typeof window !== 'undefined' &&
  typeof navigator !== 'undefined' &&
  !!navigator.mediaDevices?.getUserMedia &&
  typeof window.MediaRecorder !== 'undefined';

// Pick a mime type the browser actually supports. Chrome/Firefox prefer webm;
// Safari only does mp4. Whisper accepts both.
const pickMimeType = (): string | undefined => {
  if (typeof window.MediaRecorder === 'undefined') return undefined;
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'];
  return candidates.find((t) => window.MediaRecorder.isTypeSupported(t));
};

const extensionForMime = (mime: string): string => {
  if (mime.includes('mp4')) return 'mp4';
  if (mime.includes('ogg')) return 'ogg';
  return 'webm';
};

/**
 * Records a short audio clip with MediaRecorder, AUTO-STOPS when the speaker
 * goes quiet (voice-activity detection), and sends the clip to the backend
 * Whisper endpoint for transcription. Whisper auto-detects the spoken language.
 */
export const useVoiceRecorder = ({ onResult, transcribe }: UseVoiceRecorderOptions): UseVoiceRecorderReturn => {
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const isSupported = isRecorderSupported();

  // Web Audio bits for silence detection.
  const audioContextRef = useRef<AudioContext | null>(null);
  const vadIntervalRef = useRef<number | null>(null);
  // What the VAD saw during the current clip, read in onstop. A clip with no
  // speech must NOT be sent: on silence the STT model invents text (measured:
  // "Hallo?", "Kita", "Können Sie?"), which then lands in the input box.
  const vadRef = useRef({ active: false, heardSignal: false, spoke: false });

  // Keep latest onResult without restarting the recorder.
  const onResultRef = useRef(onResult);
  useEffect(() => {
    onResultRef.current = onResult;
  }, [onResult]);

  // Same pattern for the (optional) custom transcriber, so callers can pass
  // an inline arrow function without restarting the recorder.
  const transcribeRef = useRef(transcribe ?? transcribeAudio);
  useEffect(() => {
    transcribeRef.current = transcribe ?? transcribeAudio;
  }, [transcribe]);

  const stopVad = useCallback(() => {
    if (vadIntervalRef.current !== null) {
      window.clearInterval(vadIntervalRef.current);
      vadIntervalRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
  }, []);

  const cleanupStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  const stopRecording = useCallback(() => {
    stopVad();
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === 'inactive') return;
    try {
      recorder.stop(); // triggers onstop → transcription
    } catch {
      // ignore
    }
    mediaRecorderRef.current = null;
    setIsRecording(false);
  }, [stopVad]);

  // Stop tracks / timers if the component unmounts mid-recording.
  useEffect(() => {
    return () => {
      try {
        mediaRecorderRef.current?.stop();
      } catch {
        // ignore
      }
      stopVad();
      cleanupStream();
    };
  }, [stopVad, cleanupStream]);

  // Watch the mic level and auto-stop once the speaker finishes talking.
  const startVad = useCallback(
    (stream: MediaStream) => {
      try {
        const AudioCtx =
          window.AudioContext || (window as any).webkitAudioContext;
        if (!AudioCtx) return; // VAD unavailable — manual stop still works.
        const ctx = new AudioCtx();
        audioContextRef.current = ctx;
        // When created right after getUserMedia the context can start in a
        // 'suspended' state; without resuming, the analyser never produces
        // data and silence-detection (auto-stop) never fires.
        if (ctx.state === 'suspended') {
          ctx.resume().catch(() => {});
        }
        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 1024;
        source.connect(analyser);
        const buffer = new Uint8Array(analyser.fftSize);

        const startedAt = Date.now();
        let hasSpoken = false;
        let speechMs = 0;
        let lastSpeechAt = Date.now();
        vadRef.current.active = true;

        vadIntervalRef.current = window.setInterval(() => {
          analyser.getByteTimeDomainData(buffer);
          // RMS of the centered waveform (128 = silence midpoint).
          let sumSquares = 0;
          for (let i = 0; i < buffer.length; i++) {
            const v = (buffer[i] - 128) / 128;
            sumSquares += v * v;
          }
          const rms = Math.sqrt(sumSquares / buffer.length);
          const now = Date.now();
          // A dead analyser (context never resumed) reads a perfectly flat
          // line. Real mics never do, so this tells "silence" from "blind".
          if (rms > 0) vadRef.current.heardSignal = true;

          if (rms > SPEECH_RMS_THRESHOLD && now - startedAt >= VAD_WARMUP_MS) {
            // Any post-warmup speech frame is enough to send the clip (a short
            // "yes" must survive), but auto-stop waits for sustained speech.
            vadRef.current.spoke = true;
            speechMs += VAD_INTERVAL_MS;
            if (speechMs >= MIN_SPEECH_MS) hasSpoken = true;
            lastSpeechAt = now;
          }

          const elapsed = now - startedAt;
          const quietFor = now - lastSpeechAt;

          if (hasSpoken && quietFor >= SILENCE_HANG_MS) {
            stopRecording(); // finished talking → auto-stop + transcribe
          } else if (!hasSpoken && elapsed >= NO_SPEECH_TIMEOUT_MS) {
            stopRecording(); // never spoke → bail out (onstop skips the upload)
          } else if (elapsed >= MAX_RECORDING_MS) {
            stopRecording(); // hard cap
          }
        }, VAD_INTERVAL_MS);
      } catch {
        // If VAD setup fails, recording still works with manual stop.
      }
    },
    [stopRecording]
  );

  const startRecording = useCallback(async () => {
    if (!isSupported || isRecording || isTranscribing) return;
    setError(null);
    try {
      // Clean, mono audio gives the model the best shot at correctly detecting
      // the spoken language. Browser DSP (noise/echo/gain) removes background
      // hiss that otherwise pushes auto-detect toward the wrong language.
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      streamRef.current = stream;
      vadRef.current = { active: false, heardSignal: false, spoke: false };

      const mimeType = pickMimeType();
      const recorderOptions: MediaRecorderOptions = { audioBitsPerSecond: 128000 };
      if (mimeType) recorderOptions.mimeType = mimeType;
      const recorder = new MediaRecorder(stream, recorderOptions);
      chunksRef.current = [];

      recorder.ondataavailable = (event: BlobEvent) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onstop = async () => {
        stopVad();
        cleanupStream();
        const effectiveMime = recorder.mimeType || mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type: effectiveMime });
        chunksRef.current = [];

        if (blob.size === 0) {
          setIsTranscribing(false);
          return;
        }
        // Only skip when the VAD was working and heard no speech; if it was
        // unavailable or blind, we can't tell, so let the STT decide.
        const vad = vadRef.current;
        if (vad.active && vad.heardSignal && !vad.spoke) {
          setError("Didn't catch that. Tap the mic and try again.");
          return;
        }

        setIsTranscribing(true);
        try {
          const filename = `recording.${extensionForMime(effectiveMime)}`;
          const text = await transcribeRef.current(blob, filename);
          if (text.trim()) {
            onResultRef.current(text.trim());
          }
        } catch (e: any) {
          setError(e?.message || 'Could not transcribe audio. Please try again.');
        } finally {
          setIsTranscribing(false);
        }
      };

      recorder.start();
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
      startVad(stream);
    } catch (e: any) {
      stopVad();
      cleanupStream();
      if (e?.name === 'NotAllowedError' || e?.name === 'SecurityError') {
        setError('Microphone access was blocked. Please allow it in your browser.');
      } else {
        setError('Could not start recording. Please check your microphone.');
      }
      setIsRecording(false);
    }
  }, [isSupported, isRecording, isTranscribing, cleanupStream, stopVad, startVad]);

  const toggleRecording = useCallback(() => {
    if (isRecording) {
      stopRecording();
    } else {
      void startRecording();
    }
  }, [isRecording, startRecording, stopRecording]);

  return {
    isSupported,
    isRecording,
    isTranscribing,
    error,
    startRecording,
    stopRecording,
    toggleRecording,
  };
};

export default useVoiceRecorder;
